/**
 * Colouring curriculum (docs/COLORING-CURRICULUM.md): everything the lesson player's stage 5
 * ("Colour & shade") coach needs, DERIVED from a lesson's figure so no world content changes:
 *
 *   colourLevel      the colouring level of a lesson (its path level; playground: difficulty)
 *   COLOUR_TRACK     the 4-level skill track (flat base → light & shadow → layering → highlights)
 *   colourZones      the figure's colour zones (regions grouped by colour role, biggest first)
 *   zoneSwatches     base / shade / first-layer / highlight colours per zone, kid colour names
 *   colourHarmony    warm / cool / mixed palette + one harmony tip
 *   zonePhases       what the "colour by zones" demo shows on a zone at this level
 *   mediumTips       tips for the child's colouring tools (onboarding answer `colours`)
 *   withColourCoach  adds the derived `colourCoach` item after the lesson's colouring video chapter
 *
 * Light always comes from the TOP-LEFT (ART-BIBLE §5.1), so the shadow side is bottom-right.
 */
import { t, tf, type L } from './text';
import type { ArtFigure, ArtRegion, ArtStyleId } from './content/art/types';
import { mix, samplePath } from './content/art/geom';
import { colourStepIndex } from './content/art/compile';
import { ART_PALETTES } from './content/worlds/palettes';
import { LESSON_LEVEL } from './content/lessonLevel';
import type { Drawing, MiniUnit, MiniUnitItem } from './content/model';

export type ColourLevel = 1 | 2 | 3 | 4;
/** Onboarding answer `colours` (+ 'unknown' when the question was skipped). */
export type ColourMedium = 'pencils' | 'crayons' | 'markers' | 'none' | 'unknown';
export const COLOUR_MEDIA: ColourMedium[] = ['pencils', 'crayons', 'markers', 'none'];

export type ColourTechnique = 'insideLines' | 'oneDirection' | 'lightDirection' | 'shadowSide' | 'layering' | 'pressure' | 'texture' | 'highlight' | 'blending' | 'harmony';

export interface ColourTrackLevel {
  level: ColourLevel;
  title: L;
  goal: L;
  techniques: ColourTechnique[];
}

/** The 4-level colouring track, one level per path level (roadmap §2). */
export const COLOUR_TRACK: ColourTrackLevel[] = [
  {
    level: 1,
    title: t('Flat colour, inside the lines', 'Màu phẳng, tô trong nét'),
    goal: t('Fill each part with one colour, edges first, no white gaps.', 'Tô mỗi phần một màu, viền trước, không để hở giấy trắng.'),
    techniques: ['insideLines', 'oneDirection'],
  },
  {
    level: 2,
    title: t('Light and the shadow side', 'Ánh sáng và phía bóng'),
    goal: t('Light comes from the top-left: press harder on the bottom-right side.', 'Ánh sáng đến từ góc trên bên trái: ấn mạnh hơn ở phía dưới bên phải.'),
    techniques: ['lightDirection', 'shadowSide', 'pressure'],
  },
  {
    level: 3,
    title: t('Layers and texture', 'Tô nhiều lớp và tạo chất liệu'),
    goal: t('Start with a light layer, then build darker layers where the shadow is.', 'Tô một lớp nhạt trước, rồi chồng lớp đậm hơn ở chỗ có bóng.'),
    techniques: ['layering', 'pressure', 'texture'],
  },
  {
    level: 4,
    title: t('Highlights, blending and colour friends', 'Điểm sáng, hoà màu và bạn màu'),
    goal: t(
      'Leave a shiny spot of paper white, blend where colours meet, pick colours that go together.',
      'Chừa một chấm giấy trắng để sáng bóng, hoà màu ở chỗ hai màu gặp nhau, chọn các màu hợp nhau.',
    ),
    techniques: ['highlight', 'blending', 'harmony'],
  },
];

/** Colouring level of a lesson: its path level (1-4); playground / doodles use their difficulty. */
export function colourLevel(d: Pick<Drawing, 'id' | 'difficulty'>): ColourLevel {
  const l = LESSON_LEVEL[d.id] ?? d.difficulty;
  return Math.max(1, Math.min(4, l)) as ColourLevel;
}

/* ----------------------------------- zones ----------------------------------- */

export interface ColourZone {
  /** Colour role (e.g. "catFur"). */
  role: string;
  /** Layer indices of the figure's regions in this zone. */
  layers: number[];
  /** Part names (from the figure) in this zone. */
  parts: string[];
  /** Share of the coloured area (0..1). */
  area: number;
  /** Centre of the zone's biggest region (figure units). */
  cx: number;
  cy: number;
  /** Bounding box of the zone [x0, y0, x1, y1]. */
  box: [number, number, number, number];
  /** At least one region gets a shade crescent. */
  shaded: boolean;
  /** Where the shine (paper left white) goes: inside the biggest part, toward the light (top-left). */
  shine: [number, number];
}

type P = { x: number; y: number };
function inside(pts: P[], x: number, y: number): boolean {
  let c = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i]!;
    const b = pts[j]!;
    if (a.y > y !== b.y > y && x < ((b.x - a.x) * (y - a.y)) / (b.y - a.y) + a.x) c = !c;
  }
  return c;
}

/** Interior point of a shape nearest the light (top-left), with a margin from the outline. */
function shinePoint(pts: P[], box: [number, number, number, number]): [number, number] {
  const [x0, y0, x1, y1] = box;
  const m = Math.max(4, Math.min(x1 - x0, y1 - y0) * 0.12);
  let best: [number, number] | null = null;
  let score = Infinity;
  for (let i = 1; i < 16; i++)
    for (let j = 1; j < 16; j++) {
      const x = x0 + ((x1 - x0) * i) / 16;
      const y = y0 + ((y1 - y0) * j) / 16;
      if (!inside(pts, x, y) || !inside(pts, x - m, y) || !inside(pts, x + m, y) || !inside(pts, x, y - m) || !inside(pts, x, y + m)) continue;
      const sc = x * 0.75 + y;
      if (sc < score) {
        score = sc;
        best = [x + m * 0.4, y + m * 0.4];
      }
    }
  return best ?? [(x0 + x1) / 2, (y0 + y1) / 2];
}

function polyArea(d: string): {
  area: number;
  cx: number;
  cy: number;
  box: [number, number, number, number];
  pts: P[];
} {
  const pts = samplePath(d, 10);
  let a = 0;
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  let sx = 0;
  let sy = 0;
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i]!;
    const q = pts[(i + 1) % pts.length]!;
    a += p.x * q.y - q.x * p.y;
    x0 = Math.min(x0, p.x);
    y0 = Math.min(y0, p.y);
    x1 = Math.max(x1, p.x);
    y1 = Math.max(y1, p.y);
    sx += p.x;
    sy += p.y;
  }
  const n = Math.max(1, pts.length);
  return {
    area: Math.abs(a) / 2,
    cx: sx / n,
    cy: sy / n,
    box: [x0, y0, x1, y1],
    pts,
  };
}

/**
 * The figure's colour zones: every region grouped by colour role, ordered by area (biggest first),
 * at most `max` zones (smaller roles are dropped; they are details the child colours last).
 */
export function colourZones(fig: ArtFigure, max = 6): ColourZone[] {
  const by = new Map<string, ColourZone & { big: number }>();
  let total = 0;
  fig.layers.forEach((l, i) => {
    if (l.k !== 'region') return;
    const r = l as ArtRegion;
    const g = polyArea(r.d);
    if (!Number.isFinite(g.area) || g.area <= 0) return;
    total += g.area;
    const z = by.get(r.fill);
    if (!z) {
      by.set(r.fill, {
        role: r.fill,
        layers: [i],
        parts: r.p ? [r.p] : [],
        area: g.area,
        cx: g.cx,
        cy: g.cy,
        box: g.box,
        shaded: r.shade !== false,
        shine: shinePoint(g.pts, g.box),
        big: g.area,
      });
      return;
    }
    z.layers.push(i);
    if (r.p && !z.parts.includes(r.p)) z.parts.push(r.p);
    z.area += g.area;
    z.shaded ||= r.shade !== false;
    z.box = [Math.min(z.box[0], g.box[0]), Math.min(z.box[1], g.box[1]), Math.max(z.box[2], g.box[2]), Math.max(z.box[3], g.box[3])];
    if (g.area > z.big) Object.assign(z, { cx: g.cx, cy: g.cy, big: g.area, shine: shinePoint(g.pts, g.box) });
  });
  // Tiny parts (eyes, tongue, a bell) are details coloured last, not tappable zones.
  const sorted = [...by.values()].sort((a, b) => b.area - a.area);
  const big = sorted.filter((z, i) => i < 2 || z.area >= total * 0.015);
  return big.slice(0, max).map(({ big: _big, ...z }) => ({ ...z, area: total ? z.area / total : 0 }));
}

/* ---------------------------------- colours ---------------------------------- */

const rgb = (h: string): [number, number, number] => {
  const s = h.replace('#', '');
  const n = parseInt(s.length === 3 ? s.replace(/(.)/g, '$1$1') : s, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

/** Hue (0-360), saturation and lightness (0-1). */
export function hsl(hex: string): { h: number; s: number; l: number } {
  const [r, g, b] = rgb(hex).map((v) => v / 255) as [number, number, number];
  const mx = Math.max(r, g, b);
  const mn = Math.min(r, g, b);
  const l = (mx + mn) / 2;
  if (mx === mn) return { h: 0, s: 0, l };
  const d = mx - mn;
  const s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
  const h = mx === r ? ((g - b) / d + (g < b ? 6 : 0)) * 60 : mx === g ? ((b - r) / d + 2) * 60 : ((r - g) / d + 4) * 60;
  return { h, s, l };
}

export type ColourName = 'white' | 'cream' | 'grey' | 'black' | 'brown' | 'red' | 'orange' | 'peach' | 'yellow' | 'green' | 'teal' | 'blue' | 'purple' | 'pink';

export const COLOUR_NAMES: Record<ColourName, L> = {
  white: t('white', 'trắng'),
  cream: t('cream', 'kem'),
  grey: t('grey', 'xám'),
  black: t('dark brown', 'nâu đậm'),
  brown: t('brown', 'nâu'),
  red: t('red', 'đỏ'),
  orange: t('orange', 'cam'),
  peach: t('peach', 'hồng đào'),
  yellow: t('yellow', 'vàng'),
  green: t('green', 'xanh lá'),
  teal: t('teal', 'xanh ngọc'),
  blue: t('blue', 'xanh dương'),
  purple: t('purple', 'tím'),
  pink: t('pink', 'hồng'),
};

/** A kid-friendly name for a colour (the crayon you would pick). */
export function colourName(hex: string): ColourName {
  const { h, s, l } = hsl(hex);
  if (l > 0.93) return 'white';
  if (l < 0.2) return 'black';
  if (s < 0.14) return l > 0.82 ? 'white' : 'grey';
  if (s < 0.28 && l > 0.55 && l < 0.86) return 'grey';
  if (l > 0.86 && h >= 20 && h < 70) return 'cream';
  if (h < 14 || h >= 345) return l > 0.7 ? 'pink' : s < 0.45 && l < 0.45 ? 'brown' : 'red';
  if (h < 38) return l < 0.45 || (s < 0.45 && l < 0.6) ? 'brown' : l > 0.72 ? 'peach' : 'orange';
  if (h < 68) return l < 0.4 ? 'brown' : 'yellow';
  if (h < 160) return 'green';
  if (h < 195) return 'teal';
  if (h < 255) return 'blue';
  if (h < 300) return 'purple';
  return 'pink';
}

/** Style A shade (ART-BIBLE §5.1): mix(colour, #6D3D6B, 24 %). */
export const SHADE_TINT = '#6D3D6B';
export const SHADE_AMOUNT = 0.24;

export interface ZoneSwatch {
  role: string;
  /** Flat base colour. */
  base: string;
  /** First, light layer (layering demo). */
  light: string;
  /** Shadow-side colour (same colour, pressed harder / a darker pencil). */
  shade: string;
  /** Highlight = paper left white. */
  highlight: string;
  name: ColourName;
  /** Very light zone: "leave the paper white / colour very lightly". */
  paperWhite: boolean;
  /** Set when two swatches share a colour name: the lighter / darker one. */
  tone?: 'light' | 'dark' | 'mid';
  /** 2, 3… when several middle colours share a name. */
  nth?: number;
}

/** Kid label of a swatch: "orange", "dark orange" / "cam đậm", "light green" / "xanh lá nhạt". */
export function swatchLabel(sw: Pick<ZoneSwatch, 'name' | 'tone' | 'nth'>): L {
  const n = COLOUR_NAMES[sw.name];
  if (sw.tone === 'dark') return tf('dark {colour}', '{colour} đậm', { colour: n });
  if (sw.tone === 'light') return tf('light {colour}', '{colour} nhạt', { colour: n });
  if (sw.tone === 'mid') return sw.nth ? tf('medium {colour} {n}', '{colour} vừa {n}', { colour: n, n: sw.nth }) : tf('medium {colour}', '{colour} vừa', { colour: n });
  return n;
}

/** Resolve a zone's swatches in an art style (roles from the shared + world palettes). */
export function zoneSwatches(zones: ColourZone[], style: ArtStyleId = 'A', extra?: Record<string, string>): ZoneSwatch[] {
  const pal = { ...ART_PALETTES[style], ...(extra ?? {}) };
  const paper = pal.paper ?? '#FFF8EC';
  const out: ZoneSwatch[] = zones.map((z) => {
    const base = pal[z.role] ?? paper;
    const name = colourName(base);
    return {
      role: z.role,
      base,
      light: mix(base, '#FFFFFF', 0.45),
      shade: mix(base, SHADE_TINT, SHADE_AMOUNT + 0.08),
      highlight: '#FFFFFF',
      name,
      paperWhite: name === 'white' || name === 'cream',
    };
  });
  // Same crayon name twice (e.g. two oranges): tell them apart by lightness.
  const byName = new Map<ColourName, ZoneSwatch[]>();
  for (const sw of out) byName.set(sw.name, [...(byName.get(sw.name) ?? []), sw]);
  for (const group of byName.values()) {
    if (group.length < 2) continue;
    const sorted = group.slice().sort((a, b) => hsl(a.base).l - hsl(b.base).l);
    sorted[0]!.tone = 'dark';
    sorted[sorted.length - 1]!.tone = 'light';
  }
  return out;
}

export type HarmonyKind = 'warm' | 'cool' | 'mixed' | 'soft';

export interface Harmony {
  kind: HarmonyKind;
  /** One tip about choosing colours that go together. */
  tip: L;
}

const HARMONY_TIPS: Record<HarmonyKind, L> = {
  warm: t(
    'Warm colours (red, orange, yellow) feel cosy. A little cool blue or teal next to them makes them glow.',
    'Màu ấm (đỏ, cam, vàng) cho cảm giác ấm áp. Thêm một chút xanh dương hay xanh ngọc bên cạnh sẽ làm chúng rực lên.',
  ),
  cool: t(
    'Cool colours (blue, green, purple) feel calm. One small warm spot, like a yellow star, catches the eye.',
    'Màu lạnh (xanh dương, xanh lá, tím) cho cảm giác dịu mát. Một điểm ấm nhỏ, như ngôi sao vàng, sẽ hút mắt người xem.',
  ),
  mixed: t('Warm and cool together: let one family be the boss and use the other just a little.', 'Có cả màu ấm và màu lạnh: chọn một nhóm làm "chủ", nhóm còn lại chỉ dùng một chút.'),
  soft: t('Soft, light colours make a gentle picture. Keep the outline clear so it does not fade away.', 'Màu nhạt, dịu tạo bức tranh nhẹ nhàng. Giữ nét viền rõ để tranh không bị nhạt nhoà.'),
};

/** Warm / cool / mixed palette (area-weighted) and a matching tip. */
export function colourHarmony(swatches: ZoneSwatch[], zones?: ColourZone[]): Harmony {
  let warm = 0;
  let cool = 0;
  let any = 0;
  swatches.forEach((s, i) => {
    const w = zones?.[i]?.area ?? 1;
    const c = hsl(s.base);
    if (c.s < 0.2 || s.paperWhite) return;
    any += w;
    if (c.h < 70 || c.h >= 330) warm += w;
    else if (c.h >= 150) cool += w;
    else {
      warm += w * 0.3;
      cool += w * 0.7;
    }
  });
  const kind: HarmonyKind = any < 0.2 * (zones ? 1 : swatches.length) ? 'soft' : warm >= 0.7 * any ? 'warm' : cool >= 0.7 * any ? 'cool' : 'mixed';
  return { kind, tip: HARMONY_TIPS[kind] };
}

/* ----------------------------------- coach ----------------------------------- */

export type ZonePhase = 'light' | 'base' | 'shade' | 'highlight';

/** What the zone demo paints at a level: L1 base · L2 + shadow side · L3 light layer first · L4 + highlight. */
export function zonePhases(level: ColourLevel, opts: { shaded?: boolean; paperWhite?: boolean } = {}): ZonePhase[] {
  const out: ZonePhase[] = [];
  if (level >= 3) out.push('light');
  out.push('base');
  if (level >= 2 && opts.shaded !== false) out.push('shade');
  if (level >= 4 && !opts.paperWhite) out.push('highlight');
  return out;
}

export type CoachStep = 'palette' | 'light' | 'zones' | 'medium';

/** The coach's pages at a level (L1 skips light & shadow: flat colour first). */
export const coachSteps = (level: ColourLevel): CoachStep[] => (level >= 2 ? ['palette', 'light', 'zones', 'medium'] : ['palette', 'zones', 'medium']);

const MEDIUM_TIPS: Record<'pencils' | 'crayons' | 'markers' | 'none', Record<ColourLevel, L>> = {
  pencils: {
    1: t(
      'Colour the edge of each part first, then fill it with small side-by-side strokes. No white gaps!',
      'Tô viền từng phần trước, rồi tô kín bằng những nét nhỏ sát nhau. Không để hở giấy trắng nhé!',
    ),
    2: t(
      'Same pencil, two pressures: light on the top-left side, press harder on the bottom-right shadow side.',
      'Cùng một cây bút, hai lực ấn: nhẹ tay ở phía trên bên trái, ấn mạnh hơn ở phía bóng dưới bên phải.',
    ),
    3: t(
      'Build colour in layers: one light layer everywhere, then a second layer only where it is darker. Many light layers beat one hard press.',
      'Tô nhiều lớp: một lớp nhạt khắp nơi, rồi lớp thứ hai chỉ ở chỗ đậm hơn. Nhiều lớp nhẹ đẹp hơn một lần ấn mạnh.',
    ),
    4: t(
      'Blend where two colours meet by letting them overlap lightly. Keep a small shiny spot of paper white.',
      'Hoà màu ở chỗ hai màu gặp nhau bằng cách tô chồng nhẹ lên nhau. Chừa một chấm giấy trắng nhỏ để bóng sáng.',
    ),
  },
  crayons: {
    1: t('Use the crayon tip for edges and the side (peel a bit of paper) for big areas.', 'Dùng đầu sáp tô viền, dùng thân sáp (bóc bớt giấy) tô những vùng lớn.'),
    2: t('For the shadow side, go over it again with a darker crayon of the same colour family.', 'Ở phía bóng, tô thêm một lớp bằng cây sáp đậm hơn cùng họ màu.'),
    3: t('Texture trick: put a leaf or a coin under the paper and rub the side of the crayon over it.', 'Mẹo tạo chất liệu: đặt chiếc lá hay đồng xu dưới giấy rồi chà thân sáp lên trên.'),
    4: t(
      'Crayon cannot be erased: plan the shiny spot first and colour around it. Blend by pressing a light crayon over a dark one.',
      'Sáp màu không tẩy được: chọn trước chỗ bóng sáng và tô quanh nó. Hoà màu bằng cách ấn sáp nhạt lên trên lớp sáp đậm.',
    ),
  },
  markers: {
    1: t(
      'Markers love one direction: colour with side-by-side strokes all going the same way, so there are no stripes.',
      'Bút dạ thích một hướng: tô các nét sát nhau, cùng một chiều để không bị sọc.',
    ),
    2: t('Let the first layer dry, then go over the shadow side once more. It gets darker by itself!', 'Đợi lớp đầu khô rồi tô thêm một lần ở phía bóng. Màu sẽ tự đậm hơn!'),
    3: t('Leave a hair of space at the outline: markers spread. Work light colours first, dark ones last.', 'Chừa một khe nhỏ sát nét viền vì mực bút dạ hay loang. Tô màu nhạt trước, màu đậm sau.'),
    4: t(
      'Markers cannot be erased: leave the highlight white from the start. Blend while the ink is still wet.',
      'Bút dạ không tẩy được: chừa điểm sáng màu trắng ngay từ đầu. Hoà màu khi mực còn ướt.',
    ),
  },
  none: {
    1: t('No colours today? Your pencil works too: fill each part with soft, even grey.', 'Hôm nay chưa có màu? Bút chì cũng được: tô mỗi phần một lớp xám nhẹ, đều tay.'),
    2: t('With a pencil, shade only the bottom-right side of each part with soft lines.', 'Với bút chì, chỉ tô bóng phía dưới bên phải của từng phần bằng những nét nhẹ.'),
    3: t('Pencil layers: light grey everywhere, then a darker layer in the shadow.', 'Tô nhiều lớp bút chì: xám nhạt khắp nơi, rồi thêm lớp đậm hơn ở chỗ bóng.'),
    4: t('Leave a little paper white for the shine and smudge the shadow softly with a finger.', 'Chừa một chút giấy trắng cho chỗ sáng và dùng ngón tay miết nhẹ vùng bóng.'),
  },
};

/** Tips for the child's tools at a level: this level's tip first, then the one before it. */
export function mediumTips(medium: ColourMedium | string | undefined, level: ColourLevel): L[] {
  const m = medium === 'crayons' || medium === 'markers' || medium === 'none' ? medium : 'pencils';
  const tips = MEDIUM_TIPS[m];
  return level > 1 ? [tips[level], tips[(level - 1) as ColourLevel]] : [tips[1]];
}

/** Normalise the onboarding answer. */
export const colourMedium = (answer: unknown): ColourMedium => (answer === 'pencils' || answer === 'crayons' || answer === 'markers' || answer === 'none' ? answer : 'unknown');

/* --------------------------- lesson engine (additive) --------------------------- */

export type ColourCoachItem = Extract<MiniUnitItem, { type: 'colourCoach' }>;

/** Can this drawing get a colour coach (an art figure with a colouring chapter and ≥ 2 zones)? */
export function hasColourCoach(d: Drawing | undefined): d is Drawing & { art: ArtFigure } {
  return !!d?.art && d.steps.length >= 6 && colourStepIndex(d) >= 0 && colourZones(d.art).length >= 2;
}

/** Demo units with a fixed item list (15-type sampler, interactive lab) never get the coach. */
export const COACH_EXCLUDED = ['mu-sampler', 'mu-lab'];

/**
 * Adds the derived `colourCoach` item right after the video item that plays the colouring chapter
 * (stage 5). Content stays untouched; mini units without art (legacy drawings, quick doodles, the
 * sampler / lab) are returned as they are. Idempotent.
 */
export function withColourCoach(mu: MiniUnit, drawing: Drawing | undefined, enabled = true): MiniUnit {
  if (!enabled || COACH_EXCLUDED.includes(mu.id) || !hasColourCoach(drawing) || mu.drawingId !== drawing.id) return mu;
  if (mu.items.some((i) => i.type === 'colourCoach')) return mu;
  const ci = colourStepIndex(drawing);
  let vi = -1;
  mu.items.forEach((it, i) => {
    if (it.type === 'video' && it.drawingId === drawing.id && (it.fromStep ?? 0) <= ci && (it.toStep ?? drawing.steps.length - 1) >= ci) vi = i;
  });
  if (vi < 0) return mu;
  const item: ColourCoachItem = {
    id: `${mu.id}-colour-coach`,
    type: 'colourCoach',
    drawingId: drawing.id,
    level: colourLevel(drawing),
  };
  return {
    ...mu,
    items: [...mu.items.slice(0, vi + 1), item, ...mu.items.slice(vi + 1)],
  };
}
